// src/utils/permissions.js

// ============================================================
// STORED USER
// ============================================================

export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch (e) {
    return null;
  }
};

// ============================================================
// ROLE HELPERS
// ============================================================

export const isSuperAdmin = (user = getStoredUser()) =>
  !!user && user.type === 'SUPER_ADMIN';

export const isStaff = (user = getStoredUser()) =>
  !!user && user.type === 'STAFF';

export const isAdmin = (user = getStoredUser()) =>
  !!user && user.type === 'ADMIN';

// Admin is no longer a valid application role.
export const isValidPortalRole = (user = getStoredUser()) =>
  !!user && ['SUPER_ADMIN', 'STAFF'].includes(user.type);

// ============================================================
// PAGE ALIASES
// ============================================================

const LEGACY_ALIASES = {
  inactive: 'inactive-customers',
  placeorders: 'place-orders',
  category: 'categories',
  currency: 'currency-settings',
  lot: 'lot-tracking',
  posting: 'postings',
  staf: 'staff',
  expenses: 'expenses',

  'expenses-pk': 'expenses',
  'expenses-uk': 'expenses',

  'ordersproduct': 'orders-product',
  'orderstatus': 'orderstatusimage',
  'order-status-image': 'orderstatusimage',

  'customerpayment': 'customerspayment',
  'customers-payment': 'customerspayment',

  'order-notes': 'ordernotes',

  'image-gallery': 'imagesgallery',
  'image-galleries': 'imagesgallery',

  'implant-company': 'implant-companys',
  'implant-companies': 'implant-companys',

  'manufacturing-data': 'manufacturingdata',
  'issue-stock': 'issuestock',
  'stock-material': 'stockmaterial',

  'company-address': 'company-addresses',
  'companyaddress': 'company-addresses',
};

// ============================================================
// NORMALIZE PAGE KEY
// ============================================================

export const normalizePageKey = (key) => {
  const normalized = String(key || '')
    .trim()
    .toLowerCase();

  return LEGACY_ALIASES[normalized] || normalized;
};

// ============================================================
// PAGE GROUPS
//
// IMPORTANT:
// Keep this in the SAME order as Sidebar.
// PageAccessPicker uses this structure.
// ============================================================

export const PAGE_GROUPS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    pages: [
      {
        key: 'dashboard',
        label: 'Dashboard',
        path: '/',
      },
    ],
  },

  {
    key: 'users',
    label: 'Users',
    pages: [
      {
        key: 'users',
        label: 'Users',
        path: '/users',
      },
      {
        key: 'company-addresses',
        label: 'Company Addresses',
        path: '/company-addresses',
      },
      {
        key: 'staff',
        label: 'Staff',
        path: '/staff',
      },
      {
        key: 'customers',
        label: 'Customers',
        path: '/customers',
      },
      {
        key: 'inactive-customers',
        label: 'Inactive Customers',
        path: '/inactive-customers',
      },
    ],
  },

  {
    key: 'orders',
    label: 'Orders',
    pages: [
      {
        key: 'addresss',
        label: 'Addresses',
        path: '/Addresss',
      },
      {
        key: 'invoice-details',
        label: 'Invoice Details',
        path: '/invoice-details',
      },
      {
        key: 'customerspayment',
        label: 'Customers Payment',
        path: '/Customerspayment',
      },
      {
        key: 'ordernotes',
        label: 'Order Notes',
        path: '/Ordernotes',
      },
      {
        key: 'orderstatusimage',
        label: 'Order Status Image',
        path: '/OrderstatusImage',
      },
      {
        key: 'orders-product',
        label: 'Orders Product',
        path: '/Orders-product',
      },
      {
        key: 'payments',
        label: 'Payments',
        path: '/Payments',
      },
      {
        key: 'place-orders',
        label: 'Place Orders',
        path: '/place-orders',
      },
      {
        key: 'orders',
        label: 'Orders',
        path: '/orders',
      },
    ],
  },

  {
    key: 'products',
    label: 'Products',
    pages: [
      {
        key: 'products',
        label: 'Products',
        path: '/products',
      },
      {
        key: 'productlist',
        label: 'Product List',
        path: '/productlist',
      },
      {
        key: 'imagesgallery',
        label: 'Images Gallery',
        path: '/Imagesgallery',
      },
      {
        key: 'carts',
        label: 'Carts',
        path: '/Carts',
      },
      {
        key: 'implant-companys',
        label: 'Implant Company',
        path: '/Implant-companys',
      },
      {
        key: 'brands',
        label: 'Brands',
        path: '/Brands',
      },
      {
        key: 'categories',
        label: 'Categories',
        path: '/categories',
      },
      {
        key: 'types',
        label: 'Types',
        path: '/Types',
      },
      {
        key: 'variations',
        label: 'Variations',
        path: '/Variations',
      },
    ],
  },

  {
    key: 'management',
    label: 'Management System',
    pages: [
      {
        key: 'lot-tracking',
        label: 'Lot Tracking',
        path: '/lot-tracking',
      },
      {
        key: 'manufacturingdata',
        label: 'Manufacturing Data',
        path: '/Manufacturingdata',
      },
      {
        key: 'issuestock',
        label: 'Issue Stock',
        path: '/Issuestock',
      },
      {
        key: 'stockmaterial',
        label: 'Stock Material',
        path: '/StockMaterial',
      },
      {
        key: 'notes',
        label: 'Notes',
        path: '/Notes',
      },
    ],
  },

  {
    key: 'settings',
    label: 'Settings',
    pages: [
      {
        key: 'currency-settings',
        label: 'Currency Settings',
        path: '/currency-settings',
      },
    ],
  },
];

// ============================================================
// FLATTEN PAGE OPTIONS
//
// This gives us one array for compatibility with existing code.
// ============================================================

export const PAGE_OPTIONS = PAGE_GROUPS.flatMap((group) => group.pages);

// ============================================================
// PROFILE
//
// Profile is ALWAYS allowed.
// It is intentionally NOT part of the selectable checkboxes.
// ============================================================

export const ALWAYS_ALLOWED_KEYS = ['profile'];

// ============================================================
// SUPER ADMIN ONLY
//
// Staff should never get Shops access.
// ============================================================

export const SUPER_ADMIN_ONLY_KEYS = ['shops'];

// ============================================================
// PATH -> PAGE KEY
// ============================================================

const PATH_KEY_MAP = {
  '': 'dashboard',

  // Dashboard
  dashboard: 'dashboard',

  // Users
  users: 'users',
  'add-user': 'users',
  'edit-user': 'users',

  'company-addresses': 'company-addresses',
  'company-address': 'company-addresses',

  staff: 'staff',
  addnewstaff: 'staff',
  'edit-staff': 'staff',

  customers: 'customers',
  'edit-customer': 'customers',

  'inactive-customers': 'inactive-customers',
  inactive: 'inactive-customers',

  // Super Admin shop management
  shops: 'shops',
  'add-shop': 'shops',
  'edit-shop': 'shops',
  'shop-details': 'shops',

  // Orders
  addresss: 'addresss',

  'invoice-details': 'invoice-details',

  customerspayment: 'customerspayment',
  'add-customers-payment': 'customerspayment',

  ordernotes: 'ordernotes',

  orderstatusimage: 'orderstatusimage',

  'orders-product': 'orders-product',

  payments: 'payments',

  'place-orders': 'place-orders',

  orders: 'orders',
  'order-detail': 'orders',

  // Products
  products: 'products',

  productlist: 'productlist',

  imagesgallery: 'imagesgallery',

  carts: 'carts',

  'implant-companys': 'implant-companys',

  brands: 'brands',

  categories: 'categories',

  types: 'types',

  variations: 'variations',

  // Management
  'lot-tracking': 'lot-tracking',
  'lot-trackingdetail': 'lot-tracking',

  manufacturingdata: 'manufacturingdata',
  addmanufacturingdata: 'manufacturingdata',

  issuestock: 'issuestock',
  addissuestock: 'issuestock',

  stockmaterial: 'stockmaterial',
  addstockmaterial: 'stockmaterial',

  notes: 'notes',

  // Settings
  'currency-settings': 'currency-settings',

  // Profile
  profile: 'profile',

  // Other existing pages
  statements: 'statements',
  'statement-details': 'statements',

  postings: 'postings',
  reviews: 'reviews',
  messages: 'messages',
  settings: 'settings',
};

// ============================================================
// GET PAGE KEY FROM PATH
// ============================================================

export const getPageKeyForPath = (pathname) => {
  const clean = String(pathname || '/')
    .split('?')[0]
    .replace(/^\/shop\/\d+/i, '')
    .replace(/^\/+/, '');

  const segment = clean.split('/').filter(Boolean)[0] || '';

  const lower = segment.toLowerCase();

  if (PATH_KEY_MAP[lower] !== undefined) {
    return PATH_KEY_MAP[lower];
  }

  return normalizePageKey(lower);
};

// ============================================================
// GET ALLOWED PAGES
// ============================================================

export const getAllowedPages = (user) => {
  if (!user) return [];

  // ONLY Super Admin gets everything.
  if (user.type === 'SUPER_ADMIN') {
    return ['all'];
  }

  if (user.type !== 'STAFF') {
    return [];
  }

  if (!Array.isArray(user.allowed_pages)) {
    return [];
  }

  return user.allowed_pages.map(normalizePageKey);
};

// ============================================================
// CAN ACCESS PAGE
// ============================================================

export const canAccessPage = (user, pageKey) => {
  if (!user) return false;

  // Super Admin has complete access.
  if (user.type === 'SUPER_ADMIN') {
    return true;
  }

  // Admin has been removed.
  if (user.type !== 'STAFF') {
    return false;
  }

  const key = normalizePageKey(pageKey);

  // Profile is always accessible.
  if (ALWAYS_ALLOWED_KEYS.includes(key)) {
    return true;
  }

  // Shops is Super Admin only.
  if (SUPER_ADMIN_ONLY_KEYS.includes(key)) {
    return false;
  }

  const allowed = getAllowedPages(user);

  // IMPORTANT:
  // Staff cannot get "all" access.
  // Only explicit selected pages work.
  return allowed.includes(key);
};

// ============================================================
// CAN ACCESS PATH
// ============================================================

export const canAccessPath = (user, pathname) =>
  canAccessPage(user, getPageKeyForPath(pathname));

// ============================================================
// HOME PATH
// ============================================================

const HOME_CANDIDATES = [
  ['dashboard', '/'],

  ['orders', '/orders'],
  ['place-orders', '/place-orders'],

  ['products', '/products'],
  ['productlist', '/productlist'],

  ['categories', '/categories'],
  ['customers', '/customers'],

  ['inactive-customers', '/inactive-customers'],

  ['staff', '/staff'],
  ['users', '/users'],

  ['company-addresses', '/company-addresses'],

  ['addresss', '/Addresss'],

  ['invoice-details', '/invoice-details'],

  ['customerspayment', '/Customerspayment'],

  ['ordernotes', '/Ordernotes'],

  ['orderstatusimage', '/OrderstatusImage'],

  ['orders-product', '/Orders-product'],

  ['payments', '/Payments'],

  ['imagesgallery', '/Imagesgallery'],

  ['carts', '/Carts'],

  ['implant-companys', '/Implant-companys'],

  ['brands', '/Brands'],

  ['types', '/Types'],

  ['variations', '/Variations'],

  ['lot-tracking', '/lot-tracking'],

  ['manufacturingdata', '/Manufacturingdata'],

  ['issuestock', '/Issuestock'],

  ['stockmaterial', '/StockMaterial'],

  ['notes', '/Notes'],

  ['currency-settings', '/currency-settings'],
];

// ============================================================
// GET HOME PATH
// ============================================================

export const getHomePath = (user) => {
  if (!user) {
    return '/signin';
  }

  if (user.type === 'SUPER_ADMIN') {
    return '/';
  }

  // Admin removed.
  if (user.type !== 'STAFF') {
    return '/signin';
  }

  if (!user.shop_id) {
    return '/signin';
  }

  const base = `/shop/${user.shop_id}`;

  const match = HOME_CANDIDATES.find(([key]) =>
    canAccessPage(user, key)
  );

  if (match) {
    return `${base}${match[1]}`;
  }

  // Profile is always available.
  return `${base}/profile`;
};

// ============================================================
// PAGE GROUP HELPERS
// ============================================================

export const getAllPageKeys = () =>
  PAGE_OPTIONS.map((page) => page.key);

export const getPageCount = () => PAGE_OPTIONS.length;